import React, { useMemo, useState } from 'react';
import {
  MdSearch, MdPrint, MdOpenInNew, MdRefresh, MdCreateNewFolder, MdSchedule, MdGroup, MdFolderShared,
} from 'react-icons/md';
import { useAuth } from '../../auth';
import { useNavigate } from '../../navIntent';
import { normalize } from '../../search/searchEngine';
import PageShell, { Button } from '../../../ui/PageShell';
import { Input } from '../../../ui/Form';
import { EmptyState } from '../../../ui/Card';
import { formatRegNo } from '../../../ui/labels';
import { formatPhone } from '../../../ui/phone';
import DossierPrint from './DossierPrint';
import { initials } from './dossierModel';
import './dossier.css';

const DAYS = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
const MONTHS = ['كانون الثاني', 'شباط', 'آذار', 'نيسان', 'أيار', 'حزيران', 'تموز', 'آب', 'أيلول', 'تشرين الأول', 'تشرين الثاني', 'كانون الأول'];

export const RANGES = [
  { id: 'today', label: 'اليوم' },
  { id: 'yesterday', label: 'أمس' },
  { id: 'week', label: 'هذا الأسبوع' },
  { id: 'month', label: 'هذا الشهر' },
];

const WHO_KEY = (uid) => `mtz.dossiers.who.${uid ?? 'anon'}`;

/**
 * The day boundaries on the office computer's own clock. The week starts
 * on Sunday, like the school week (and لوحة المراقبة's «هذا الأسبوع»).
 * `from` is the earliest start of all four ranges, so ONE request covers
 * every chip on the screen.
 */
export function dossierRanges(now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const shift = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
  const tomorrow = shift(today, 1);
  const yesterday = shift(today, -1);
  const week = shift(today, -today.getDay());
  const month = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    from: new Date(Math.min(yesterday, week, month)),
    to: tomorrow,
    spans: {
      today: [today, tomorrow],
      yesterday: [yesterday, today],
      week: [week, tomorrow],
      month: [month, tomorrow],
    },
  };
}

const inSpan = (iso, [a, b]) => { const t = Date.parse(iso); return t >= a.getTime() && t < b.getTime(); };
const clock = (iso) => new Date(iso).toLocaleTimeString('ar-SY-u-nu-latn', { hour: '2-digit', minute: '2-digit' });

function dayLabel(d, today) {
  const diff = Math.round((today - d) / 86400000);
  const date = `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
  if (diff === 0) return `اليوم — ${date}`;
  if (diff === 1) return `أمس — ${date}`;
  return date;
}

/**
 * «الإضبارات» — pupils by the day their dossier was opened: today,
 * yesterday, this week, this month; «إضباراتي» (the ones I added) or
 * everyone's. Print or open any of them from here.
 *
 * `feed` comes from StudentDirectory (it also drives the tab's badge):
 *   { rows, loading, error, reload, revalidating, ranges }
 */
export default function DossierList({ tabs, feed }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [range, setRange] = useState('today');
  const [who, setWho] = useState(() => {
    try { return localStorage.getItem(WHO_KEY(user?.id)) || 'mine'; } catch { return 'mine'; }
  });
  const [q, setQ] = useState('');
  const [printing, setPrinting] = useState(null);
  const [limit, setLimit] = useState(80);

  const chooseWho = (v) => {
    setWho(v);
    try { localStorage.setItem(WHO_KEY(user?.id), v); } catch { /* ignore */ }
  };

  const rows = useMemo(() => feed.rows || [], [feed.rows]);
  const { spans } = feed.ranges;
  const mineOnly = useMemo(() => rows.filter((r) => r.created_by === user?.id), [rows, user]);
  const base = who === 'mine' ? mineOnly : rows;

  const counts = useMemo(() => Object.fromEntries(
    RANGES.map((r) => [r.id, base.filter((x) => inSpan(x.created_at, spans[r.id])).length])
  ), [base, spans]);

  const inRange = useMemo(() => base.filter((x) => inSpan(x.created_at, spans[range])), [base, spans, range]);

  const filtered = useMemo(() => {
    const nq = normalize(q.trim());
    const digits = q.replace(/\D/g, '').replace(/^0+/, '');
    if (!nq) return inRange;
    return inRange.filter((r) => {
      const hay = normalize([r.name, r.father_name, r.mother_name, r.section_label, r.created_by_name, r.national_id].filter(Boolean).join(' '));
      if (nq.split(' ').every((w) => hay.includes(w))) return true;
      return digits.length >= 3 && [r.registration_number, r.father_phone, r.mother_phone, r.student_phone]
        .some((v) => String(v || '').replace(/\D/g, '').includes(digits));
    });
  }, [inRange, q]);

  // Who opened how many, in this range (everyone's view only).
  const byRegistrar = useMemo(() => {
    if (who === 'mine') return [];
    const m = new Map();
    inRange.forEach((r) => {
      const k = r.created_by_name || 'غير معروف';
      m.set(k, (m.get(k) || 0) + 1);
    });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [inRange, who]);

  // Grouped by the day they were opened, newest day first.
  const groups = useMemo(() => {
    const today = spans.today[0];
    const out = [];
    let cur = null;
    filtered.slice(0, limit).forEach((r) => {
      const d = new Date(r.created_at);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!cur || cur.key !== key) {
        cur = { key, label: dayLabel(new Date(d.getFullYear(), d.getMonth(), d.getDate()), today), rows: [] };
        out.push(cur);
      }
      cur.rows.push(r);
    });
    return out;
  }, [filtered, limit, spans]);

  const rangeWord = RANGES.find((r) => r.id === range)?.label || '';

  return (
    <PageShell
      title="شؤون الطلاب"
      description="الإضبارات المفتوحة مؤخراً — للمراجعة والطباعة وفتح ملف الطالب"
      actions={(
        <>
          <Button variant="secondary" onClick={feed.reload} loading={feed.revalidating}>
            <MdRefresh size={16} /> تحديث
          </Button>
          <Button variant="success" onClick={() => navigate('student-directory', { view: 'new' })}>
            <MdCreateNewFolder size={16} /> إضبارة جديدة
          </Button>
        </>
      )}
    >
      {tabs}

      {feed.error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{feed.error}</div></div>}

      <div className="dl-toolbar">
        <div className="dl-ranges" role="tablist">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              role="tab"
              aria-selected={range === r.id}
              className={`dl-range${range === r.id ? ' is-active' : ''}`}
              onClick={() => { setRange(r.id); setLimit(80); }}
            >
              <span>{r.label}</span>
              <b>{counts[r.id] ?? 0}</b>
            </button>
          ))}
        </div>
        <div className="ui-segmented" role="tablist">
          {[{ v: 'mine', label: 'إضباراتي' }, { v: 'all', label: 'كل الإضبارات' }].map((o) => (
            <button
              key={o.v}
              type="button"
              className={`ui-segmented-btn ${who === o.v ? 'is-active' : ''}`}
              onClick={() => chooseWho(o.v)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="dl-filters">
        <div className="sd-search-wrap dl-search">
          <span className="sd-search-icon"><MdSearch size={17} /></span>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث في هذه الإضبارات: الاسم، رقم التسجيل، الأب، الهاتف..."
          />
        </div>
        {byRegistrar.length > 0 && (
          <div className="dl-registrars">
            <MdGroup size={16} />
            {byRegistrar.map(([name, n]) => (
              <span key={name} className="dl-registrar"><b>{n}</b> {name}</span>
            ))}
          </div>
        )}
      </div>

      {feed.loading && !rows.length ? (
        <div className="dl-empty">جارٍ التحميل...</div>
      ) : filtered.length === 0 ? (
        <div className="dl-empty-card">
          <EmptyState
            icon={<MdFolderShared size={42} />}
            title={q ? 'لا توجد نتائج مطابقة' : `لا توجد إضبارات ${who === 'mine' ? 'أضفتها ' : ''}${rangeWord}`}
            action={!q && (
              <Button variant="success" onClick={() => navigate('student-directory', { view: 'new' })}>
                <MdCreateNewFolder size={16} /> إضبارة جديدة
              </Button>
            )}
          />
        </div>
      ) : (
        <div className="dl-groups">
          {groups.map((g) => (
            <section key={g.key} className="dl-group">
              <header className="dl-day">
                <span>{g.label}</span>
                <b>{g.rows.length}</b>
              </header>
              {g.rows.map((r) => (
                <div
                  key={r.id}
                  className="dl-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate('student-directory', { studentId: r.id })}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate('student-directory', { studentId: r.id }); }}
                >
                  <span className={`dz-avatar is-small${r.gender ? ` is-${r.gender}` : ''}`}>{initials(r.name)}</span>
                  <div className="dl-row-main">
                    <div className="dl-row-name">
                      {r.name}
                      <span className="dl-regno">{formatRegNo(r.registration_number)}</span>
                      {r.status !== 'active' && <span className="ui-badge ui-badge-neutral">مغادر</span>}
                    </div>
                    <div className="dl-row-sub">
                      {r.section_label
                        ? <span>{r.section_label}</span>
                        : <span className="ui-badge ui-badge-warning">بلا شعبة</span>}
                      {r.father_name && <span>الأب: {r.father_name}</span>}
                      {(r.father_phone || r.mother_phone) && (
                        <bdi dir="ltr">{formatPhone(r.father_phone || r.mother_phone)}</bdi>
                      )}
                      {r.sibling_count > 0 && <span className="dl-sibs">إخوة في المدرسة: {r.sibling_count}</span>}
                    </div>
                    <div className="dl-row-meta">
                      <MdSchedule size={14} /> {clock(r.created_at)}
                      {r.created_by_name && <> · سجّله <b>{r.created_by_name}</b></>}
                    </div>
                  </div>
                  <div className="dl-row-actions" onClick={(e) => e.stopPropagation()} role="presentation">
                    <button type="button" className="dl-icon-btn" title="طباعة الإضبارة" onClick={() => setPrinting(r.id)}>
                      <MdPrint size={18} />
                    </button>
                    <button
                      type="button"
                      className="dl-icon-btn"
                      title="فتح ملف الطالب"
                      onClick={() => navigate('student-directory', { studentId: r.id })}
                    >
                      <MdOpenInNew size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </section>
          ))}
          {filtered.length > limit && (
            <div className="dl-empty">
              <Button variant="secondary" onClick={() => setLimit((n) => n + 100)}>
                عرض المزيد ({filtered.length - limit} متبقٍ)
              </Button>
            </div>
          )}
        </div>
      )}

      {printing && <DossierPrint studentId={printing} onClose={() => setPrinting(null)} />}
    </PageShell>
  );
}
